import { InfiniteScroll } from "../infinite-scroll";
import { LoadingIcon } from "../../../assets";
import { get } from "lodash";
import { ColumnType, TableSettingsType } from "./types";
import {
  Table as MaterialTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import React, { useCallback, useMemo } from "react";

export const Table = ({
  data,
  hover,
  click,
  columns,
  message,
  headerClassName,
  rowStyles,
  getNextPage,
  isLoading,
  hasMorePages,
  maxHeight,
}: TableSettingsType): React.ReactElement => {
  const headers = useMemo(
    (): Array<React.ReactElement> =>
      columns.map((column: ColumnType, key) => (
        <TableCell
          key={key}
          align="center"
          className={[
            "cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap border-b-0 bg-[#FFFFFF] px-[5px] py-[2px] text-[12px] font-[700] text-[#000000]",
            headerClassName,
          ].join(" ")}
          width={column.width}
          style={{
            maxWidth: column.maxWidth,
          }}
        >
          {column.header}
        </TableCell>
      )),
    [columns, headerClassName]
  );

  const renderValue = useCallback(
    (
      { prop, component }: ColumnType,
      element: Record<string, unknown>
    ): React.ReactElement => {
      if (component) {
        const { component: Component, props } = component;

        return (
          <Component
            data={prop === "all" ? element : get(element, prop, "")}
            {...props}
          />
        );
      }

      return (element[prop] || "- -") as React.ReactElement;
    },
    []
  );

  const renderRow = useCallback(
    (element: Record<string, unknown>, index: number): React.ReactElement => {
      const cells = columns.map((column) => ({
        ...column,
        prop: get(column, "prop", "all"),
      }));

      return (
        <TableRow
          style={{ padding: "10px 16px", ...rowStyles }}
          hover={hover}
          key={index}
          onClick={() => (click ? click(element) : null)}
        >
          {cells.map(
            (
              { align = "inherit", width, maxWidth, ...cell }: ColumnType,
              i
            ) => (
              <TableCell
                className={`mobile-table-cell text-ellipsis whitespace-pre-line break-words ${
                  hover ? "cursor-pointer" : ""
                } overflow-[inherit] m-0 border-b-0 p-[5px] text-[12px] text-[#707070]`}
                key={i}
                align={align}
                width={width}
                style={{
                  maxWidth: maxWidth,
                  overflow: "hidden",
                }}
              >
                {renderValue(cell, element)}
              </TableCell>
            )
          )}
        </TableRow>
      );
    },
    [click, columns, hover, renderValue, rowStyles]
  );

  return (
    <TableContainer style={{ maxHeight: maxHeight }}>
      <MaterialTable aria-label="sticky table" stickyHeader>
        <TableHead>
          <TableRow>{headers}</TableRow>
        </TableHead>
        <TableBody className="mobile-stripped">
          <InfiniteScroll
            isLoading={isLoading}
            hasNextPage={hasMorePages}
            loadMore={getNextPage}
            data={data}
            renderItem={renderRow}
            customLoadMoreElement={(ref) => (
              <TableRow ref={ref}>
                <TableCell colSpan={columns.length} align="center">
                  <section className="grid h-[30px] w-full place-content-center place-items-center">
                    {isLoading && <LoadingIcon />}
                  </section>
                </TableCell>
              </TableRow>
            )}
          />
          {data.length === 0 && !isLoading && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                {message}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </MaterialTable>
    </TableContainer>
  );
};
